import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  FileText, FileSignature, Send, Check, CheckCircle, XCircle, MousePointer,
  CreditCard, Circle, Loader2,
} from "lucide-react";

const ICON_MAP = {
  "file-text": FileText,
  "file-signature": FileSignature,
  "send": Send,
  "check": Check,
  "check-circle": CheckCircle,
  "x-circle": XCircle,
  "mouse-pointer": MousePointer,
  "credit-card": CreditCard,
  "circle": Circle,
};

const TONE_MAP = {
  emerald: { dot: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50" },
  green:   { dot: "bg-green-600",   text: "text-green-800",   bg: "bg-green-50" },
  blue:    { dot: "bg-blue-500",    text: "text-blue-700",    bg: "bg-blue-50" },
  violet:  { dot: "bg-violet-500",  text: "text-violet-700",  bg: "bg-violet-50" },
  amber:   { dot: "bg-amber-500",   text: "text-amber-800",   bg: "bg-amber-50" },
  red:     { dot: "bg-red-500",     text: "text-red-700",     bg: "bg-red-50" },
  slate:   { dot: "bg-slate-400",   text: "text-slate-700",   bg: "bg-slate-50" },
};

export default function OnboardingTimeline({ sfaNumber, showCurrentState = false }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/aus/onboarding-timeline", { params: { sfa_number: sfaNumber } });
        if (!cancelled) setEvents(r.data?.events || []);
      } catch (e) {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sfaNumber]);

  if (loading) {
    return <div className="py-6 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>;
  }
  if (events.length === 0) {
    return <p className="text-xs text-muted-foreground text-center py-6">No events for {sfaNumber} yet</p>;
  }

  const currentState = showCurrentState ? deriveCurrentState(events) : null;

  return (
    <div className="space-y-3" data-testid="onboarding-timeline-wrapper">
      {currentState && <CurrentStateBanner state={currentState} />}
      <ol className="relative border-l-2 border-slate-200 pl-4 ml-2 space-y-3" data-testid="onboarding-timeline">
        {events.map((ev, i) => {
          const Icon = ICON_MAP[ev.icon] || Circle;
          const tone = TONE_MAP[ev.tone] || TONE_MAP.slate;
          return (
            <li key={`${ev.at}-${i}`} className="relative" data-testid={`timeline-event-${i}`}>
              <span className={`absolute -left-[22px] top-1 w-3.5 h-3.5 rounded-full border-2 border-white ${tone.dot} flex items-center justify-center`}>
                <Icon className="w-2 h-2 text-white" />
              </span>
              <div className={`p-2 rounded-md ${tone.bg} border border-slate-200`}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className={`text-xs font-semibold ${tone.text}`}>{ev.label}</span>
                  <span className="text-[10px] text-slate-500 whitespace-nowrap" title={ev.at}>{fmtTime(ev.at)}</span>
                </div>
                {ev.details && <p className="text-[11px] text-slate-600 mt-0.5">{ev.details}</p>}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/**
 * Derive a "current state" snapshot from the timeline events so operators can
 * see at a glance where this onboarding is stuck.
 */
function deriveCurrentState(events) {
  const has = (kind) => events.some((e) => e.kind === kind);
  const lastOf = (kinds) => {
    for (let i = events.length - 1; i >= 0; i--) {
      if (kinds.includes(events[i].kind)) return events[i];
    }
    return null;
  };

  const mandateAuthorised = has("mandate_authorised");
  const envelopeSigned = has("envelope_signed");

  // Terminal states
  if (mandateAuthorised && envelopeSigned) {
    return {
      tone: "green",
      title: "Fully onboarded ✓",
      detail: "Customer has signed the agreement and authorised the direct debit.",
    };
  }

  // Failures
  const failed = lastOf(["envelope_declined", "sms_failed"]);
  if (failed) {
    return {
      tone: "red",
      title: failed.kind === "envelope_declined" ? "Document declined / voided" : "SMS delivery failed",
      detail: failed.details || "Action required to recover.",
    };
  }

  // In-progress — find the latest event and infer what's pending
  const last = events[events.length - 1];
  if (!last) {
    return { tone: "slate", title: "Not started", detail: "" };
  }

  const elapsed = humanElapsed(last.at);

  const blueprint = {
    contract_generated: {
      tone: "amber",
      title: "Awaiting envelope dispatch",
      detail: `Contract generated ${elapsed} — DocuSign envelope not yet sent.`,
    },
    envelope_sent: {
      tone: "amber",
      title: envelopeSigned ? "Awaiting mandate" : "Awaiting signature",
      detail: envelopeSigned
        ? `Envelope sent ${elapsed} — mandate not yet created.`
        : `Envelope sent ${elapsed} — customer has not signed yet.`,
    },
    docusign_sms_sent: {
      tone: "amber",
      title: "Awaiting signature",
      detail: `Signing SMS sent ${elapsed} — customer has not signed yet.`,
    },
    sms_delivered: {
      tone: "amber",
      title: mandateAuthorised ? "Awaiting signature" : "Awaiting authorisation",
      detail: `SMS delivered ${elapsed} — waiting on customer.`,
    },
    link_tapped: {
      tone: "amber",
      title: mandateAuthorised ? "Awaiting signature" : "Customer engaged · awaiting authorisation",
      detail: `Customer opened the link ${elapsed} — not yet completed.`,
    },
    envelope_signed: {
      tone: "amber",
      title: "Document signed · awaiting mandate authorisation",
      detail: `Signed ${elapsed}.`,
    },
    mandate_authorised: {
      tone: "amber",
      title: "Mandate authorised · awaiting signature",
      detail: `Authorised ${elapsed} — DocuSign still pending.`,
    },
    mandate_created: {
      tone: "amber",
      title: envelopeSigned ? "Awaiting authorisation" : "Mandate issued · awaiting customer authorisation",
      detail: `Mandate created ${elapsed} — customer not yet authorised.`,
    },
    mandate_sms_sent: {
      tone: "amber",
      title: "Awaiting authorisation",
      detail: `Mandate SMS sent ${elapsed} — customer hasn't authorised yet.`,
    },
  };
  return blueprint[last.kind] || { tone: "slate", title: "In progress", detail: `Last activity ${elapsed}.` };
}

function humanElapsed(iso) {
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  } catch {
    return "recently";
  }
}

function CurrentStateBanner({ state }) {
  const tone = TONE_MAP[state.tone] || TONE_MAP.slate;
  return (
    <div
      className={`p-2.5 rounded-md border ${tone.bg} border-slate-300 flex items-start gap-2.5`}
      data-testid="timeline-current-state"
    >
      <div className={`w-2.5 h-2.5 rounded-full ${tone.dot} mt-1 flex-shrink-0 ${state.tone === "amber" ? "animate-pulse" : ""}`} />
      <div className="min-w-0 flex-1">
        <p className={`text-xs font-semibold ${tone.text}`}>{state.title}</p>
        {state.detail && <p className="text-[11px] text-slate-600 mt-0.5">{state.detail}</p>}
      </div>
    </div>
  );
}

function fmtTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-AU", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}
